import { useCallback, useState } from 'react';

import { Box, Form, Toast, useToast } from '@zillow/constellation-10';

import { TimeZoneDropdownOptions } from '@zg-rentals/touring';
import { ActionMetadataEntryPoint } from '@zg-rentals/ts-z-calendar-client';

import { useTourSchedulingContext } from '../../../../contexts/TourSchedulingContext';

import TourSchedulingTimeZoneBody from './components/TourSchedulingTimeZoneBody';

import { wizardFlexContainerStyles } from '../../../../styles/wizard';

import SpinnerContainer from '../../../SpinnerContainer';
import PageWizard from '../../../Wizard/PageWizard';

import type { ComboboxOptionObjectInterface, ComboboxOptionType } from '@zillow/constellation-10';
import type { TimeZoneIds } from '@zg-rentals/touring';
import type { TourDuration } from '@zg-rentals/ts-z-calendar-client';
import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const TourSchedulingTimezone = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const { enqueueToast } = useToast();
  const {
    selectedBookingMethod,
    selectedTimeZone,
    setSelectedTimeZone,
    selectedTourDuration,
    setSelectedTourDuration,
    tourCalendarApiHook,
  } = useTourSchedulingContext();

  const [isNavigating, setIsNavigating] = useState(false);

  const onNextClick = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      if (
        selectedBookingMethod &&
        selectedTimeZone &&
        (selectedBookingMethod !== tourCalendarApiHook.tourConfirmationType ||
          selectedTimeZone !== tourCalendarApiHook.timeZone ||
          selectedTourDuration !== tourCalendarApiHook.tourDuration)
      ) {
        await tourCalendarApiHook.enableTouringForListing(
          selectedBookingMethod,
          selectedTimeZone,
          selectedTourDuration,
          ActionMetadataEntryPoint.NEW_LISTING,
        );
      }
      onGoToNextStep?.();
    } catch {
      enqueueToast(
        <Toast tone="critical">Your tour setting changes couldn’t be saved. Try again in a few moments.</Toast>,
      );
    } finally {
      setIsNavigating(false);
    }
  };

  const handleTimeZoneChange = useCallback(
    (option: ComboboxOptionType) => {
      const timeZoneId = TimeZoneDropdownOptions.find(
        (timeZoneOption) => timeZoneOption.value === (option as ComboboxOptionObjectInterface).value,
      )?.value;
      setSelectedTimeZone(timeZoneId as TimeZoneIds);
    },
    [setSelectedTimeZone],
  );

  const handleTourDurationChange = (comboBoxOption: ComboboxOptionType) => {
    const option = comboBoxOption as ComboboxOptionObjectInterface;
    setSelectedTourDuration(option.value as TourDuration);
  };

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      isNextDisabled={!selectedTimeZone || tourCalendarApiHook.isEnableTouringForListingLoading || isNavigating}
    >
      <div data-testid="TourSchedulingTimezone">
        {tourCalendarApiHook.isGetTourConfigAndAvailabilityLoading ? (
          <SpinnerContainer />
        ) : (
          <Box css={wizardFlexContainerStyles}>
            <Form asChild onSubmit={(e) => e.preventDefault()}>
              <TourSchedulingTimeZoneBody
                selectedTimeZone={selectedTimeZone}
                onSelectTimeZone={handleTimeZoneChange}
                selectedTourDuration={selectedTourDuration}
                onSelectTourDuration={handleTourDurationChange}
              />
            </Form>
          </Box>
        )}
      </div>
    </PageWizard>
  );
};

export default TourSchedulingTimezone;
